import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { AuthError, AuthField, AuthHeading, authLinkClass, AuthPasswordField, AuthSubmit, loginSchema, useAuth, useAuthNavigation } from '@/app/auth';

export const Route = createFileRoute('/login/')({
  validateSearch: (search: Record<string, unknown>): { force?: true } => ('force' in search ? { force: true } : {}),
  component: LoginScreen,
});

type Values = z.infer<ReturnType<typeof loginSchema>>;

function LoginScreen() {
  const { t } = useLingui();
  const { session, status, pending, error } = useAuth();
  const { force } = Route.useSearch();
  const navigate = useNavigate();
  const follow = useAuthNavigation(session);
  const form = useForm<Values>({ resolver: zodResolver(loginSchema()), defaultValues: { email: '', password: '' } });

  // Já autenticado e sem `?force`, vai para o início (paridade com LoginFormContainer).
  useEffect(() => {
    if (status === 'signedIn' && !force) void navigate({ to: '/', replace: true });
  }, [status, force, navigate]);

  // Com mouse e teclado, o cursor já começa no e-mail; no celular, o teclado só abre no toque.
  useEffect(() => {
    if (globalThis.matchMedia('(pointer: fine)').matches) form.setFocus('email');
  }, [form]);

  const onSubmit = form.handleSubmit(async ({ email, password }) => {
    await follow(await session.signIn(email, password));
  });

  return (
    <form className="flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthHeading title={<Trans>Iniciar sessão</Trans>}>
        <Trans>Entre com seu e-mail e senha para continuar.</Trans>
      </AuthHeading>
      <AuthError message={error} />
      <div className="flex flex-col gap-4">
        <AuthField
          label={t`E-mail`}
          type="email"
          inputMode="email"
          icon={Mail}
          autoComplete="username"
          registration={form.register('email')}
          error={form.formState.errors.email}
        />
        <AuthPasswordField label={t`Senha`} autoComplete="current-password" registration={form.register('password')} error={form.formState.errors.password} />
        <div className="-mt-1 flex justify-end">
          <Link to="/login/recuperar-senha" className={authLinkClass}>
            <Trans>Esqueceu sua senha?</Trans>
          </Link>
        </div>
      </div>
      <AuthSubmit pending={pending}>
        <Trans>Acessar</Trans>
      </AuthSubmit>
    </form>
  );
}
